import type React from 'react';
import { useState, useEffect } from 'react';

import { Checkbox, FormControlLabel } from '@mui/material';
import { useTranslate } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import DTMFInput from '../../components/DTMFInput';
import HTTPInput from '../../components/HTTPInput';
import SoundfileList from '../../components/SoundfileList';
import NodeControlForm from '../NodeControlForm';

import type { HTTPData, DTMFInputData, Header, NodeControlsProps } from '../../dataProvider/types';
import type { Soundfile } from '@ragu-components/ord-types';

export interface PlaybackType extends HTTPData, DTMFInputData {
  audioSource: Soundfile | null;
}

interface PlaybackControlsProps extends NodeControlsProps {
  updateNodeData: (data: Record<string, unknown>) => void;
  data: PlaybackType;
}

export default function PlaybackControls({ updateNodeData, data }: PlaybackControlsProps): React.ReactElement {
  const [audioSource, setAudioSource] = useState<Soundfile | null>(
    data.audioSource !== undefined ? data.audioSource : null
  );
  const [url, setUrl] = useState<string>(data.url ?? '');
  const [method, setMethod] = useState<'get' | 'post'>(data.method ?? 'get');
  const [headers, setHeaders] = useState<Header[]>(data.headers ?? []);
  const [sslVerify, setSSLVerify] = useState<boolean>(data.sslVerify ?? true);
  const [useUrlSource, setUseUrlSource] = useState<boolean>(
    data.audioSource === undefined || data.audioSource === null ? data.url !== undefined || data.url === '' : false
  );
  const [useDTMFGathering, setUseDTMFGathering] = useState<boolean>(
    data.outputPropName !== null && data.outputPropName !== undefined && data.outputPropName !== ''
  );
  const [maxDigits, setMaxDigits] = useState<number>(data.maxDigits ?? 128);
  const [outputPropName, setOutputPropName] = useState<string>(data.outputPropName ?? '');
  const [terminators, setTerminators] = useState<string>(data.terminators ?? '#');
  const [timeout, setTimeout] = useState<number>(data.timeout ?? 5000);
  const translate = useTranslate();

  const onUpdateHTTPInputData = ({ url, method, headers, sslVerify }: HTTPData): void => {
    flowchartLogger.debug('PlaybackControls: onUpdateHTTPInputData', { url, method, headers, sslVerify });
    setUrl(url);
    setMethod(method);
    setHeaders(headers);
    setSSLVerify(sslVerify);
    setAudioSource(null);
  };

  const onUpdateDTMFInputData = ({ maxDigits, outputPropName, terminators, timeout }: DTMFInputData): void => {
    flowchartLogger.debug('PlaybackControls: onUpdateDTMFInputData', {
      maxDigits,
      outputPropName,
      terminators,
      timeout,
    });
    setMaxDigits(maxDigits);
    setOutputPropName(outputPropName);
    setTerminators(terminators);
    setTimeout(timeout);
  };

  const onPlaybackDataUpdate = (event: React.FormEvent): void => {
    event.preventDefault();
    flowchartLogger.debug('PlaybackControls: onPlaybackDataUpdate', {
      url,
      method,
      headers,
      sslVerify,
      audioSource,
      maxDigits,
      outputPropName,
      terminators,
      timeout,
    });
    if (audioSource === null && url === '') {
      flowchartLogger.debug('PlaybackControls: audioSource and url are undefined or empty. Update cancelled.');
      return;
    }

    updateNodeData({ url, method, headers, sslVerify, audioSource, maxDigits, outputPropName, terminators, timeout });
  };

  const onToggleUrlSourceChange = (): void => {
    const newUseUrlSource = !useUrlSource;
    setUseUrlSource(newUseUrlSource);
    if (!newUseUrlSource) {
      flowchartLogger.debug(
        'PlaybackControls: After toggle url source clearing url, method, headers, sslVerify, audioSource'
      );
      setUrl('');
      setMethod('get');
      setHeaders([]);
      setSSLVerify(true);
      setAudioSource(null);
    }
  };

  const onToggleDTMFvisibility = (): void => {
    const newUseDTMFGathering = !useDTMFGathering;
    setUseDTMFGathering(newUseDTMFGathering);
    if (!newUseDTMFGathering) {
      flowchartLogger.debug(
        'PlaybackControls: After toggle DTMF gathering clearing maxDigits, outputPropName, terminators'
      );
      setMaxDigits(128);
      setOutputPropName('');
      setTerminators('#');
      setTimeout(5000);
    }
  };

  useEffect(() => {
    flowchartLogger.debug('PlaybackControls: autosave', { url, method, headers, sslVerify, audioSource });
    updateNodeData({ url, method, headers, sslVerify, audioSource, maxDigits, outputPropName, terminators, timeout });
  }, [url, method, headers, sslVerify, audioSource, maxDigits, outputPropName, terminators, timeout, updateNodeData]);

  return (
    <NodeControlForm
      onSubmit={onPlaybackDataUpdate}
      isDisabled={!useUrlSource && audioSource === null}
      data={data}
      updateNodeData={updateNodeData}
    >
      {useDTMFGathering && (
        <DTMFInput
          maxDigits={maxDigits}
          outputPropName={outputPropName}
          terminators={terminators}
          timeout={timeout}
          onUpdate={onUpdateDTMFInputData}
        />
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={useDTMFGathering}
            onChange={() => {
              onToggleDTMFvisibility();
            }}
          />
        }
        label={translate('callflows.nodes.playback.useDTMFGathering')}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={useUrlSource}
            onChange={() => {
              onToggleUrlSourceChange();
            }}
          />
        }
        label={translate('callflows.nodes.playback.useUrlSource')}
      />
      {useUrlSource ? (
        <HTTPInput updateHTTPInputData={onUpdateHTTPInputData} data={{ url, method, headers, sslVerify }} />
      ) : (
        <SoundfileList
          soundfile={audioSource !== null ? audioSource.id : ''}
          label={translate('callflows.nodes.playback.soundfile')}
          setSoundfile={setAudioSource}
        />
      )}
    </NodeControlForm>
  );
}
